import { collection, getDocs, query, where } from 'firebase/firestore';

import { db } from '@/firebase-config';
import { normalizeTextRanges } from '@/features/notes/line-note-text-selection.ts';
import type { LineIssueType, LineNote } from '@/features/notes/types/line-notes';
import { buildProjectSubcollectionPath, type ProjectScope } from '@/features/sync/project-scope';

import type { LineNotesClient } from '../contracts/line-notes-client';

type FirestoreDocumentSnapshot = {
  id: string;
  data(): unknown;
};

type FirestoreCollectionSnapshot = {
  docs?: FirestoreDocumentSnapshot[];
  forEach?: (callback: (doc: FirestoreDocumentSnapshot) => void) => void;
};

const LINE_ISSUE_TYPES = new Set<LineIssueType>([
  'transposed',
  'called',
  'dropped',
  'paraphrased',
  'late_pickup',
  'jumped',
]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function stringValue(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function isInteger(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value);
}

function actorIdValue(value: unknown): LineNote['actorId'] {
  return typeof value === 'string' ? value : null;
}

function issueTypeValue(value: unknown): LineIssueType {
  return typeof value === 'string' && LINE_ISSUE_TYPES.has(value as LineIssueType)
    ? value as LineIssueType
    : 'called';
}

function stringSnapshot(name: string, value: unknown): Partial<LineNote> {
  return typeof value === 'string' ? { [name]: value } : {};
}

function sourceTextRangeSnapshot(value: unknown): Partial<LineNote> {
  if (!isRecord(value)) {
    return {};
  }

  const { start, end } = value;
  if (!isInteger(start) || !isInteger(end) || start < 0 || start >= end) {
    return {};
  }

  return { sourceTextRange: { start, end } };
}

function highlightedWordRangesSnapshot(value: unknown, lineTextSnapshot: string): Partial<LineNote> {
  if (!Array.isArray(value)) {
    return {};
  }

  const ranges = value.flatMap(range => {
    if (!isRecord(range) || !isInteger(range.start) || !isInteger(range.end)) {
      return [];
    }
    return [{ start: range.start, end: range.end }];
  });

  return { highlightedWordRanges: normalizeTextRanges(ranges, lineTextSnapshot) };
}

function lineNoteFromSnapshot(docSnap: FirestoreDocumentSnapshot): LineNote {
  const data = docSnap.data();
  const record = isRecord(data) ? data : {};

  return {
    id: docSnap.id,
    scriptBlockId: stringValue(record.scriptBlockId),
    characterId: stringValue(record.characterId),
    actorId: actorIdValue(record.actorId),
    rehearsalDate: stringValue(record.rehearsalDate),
    issueType: issueTypeValue(record.issueType),
    createdAt: stringValue(record.createdAt),
    createdBy: stringValue(record.createdBy),
    ...stringSnapshot('lineTextSnapshot', record.lineTextSnapshot),
    ...sourceTextRangeSnapshot(record.sourceTextRange),
    ...highlightedWordRangesSnapshot(record.highlightedWordRanges, stringValue(record.lineTextSnapshot)),
    ...stringSnapshot('comment', record.comment),
    ...stringSnapshot('characterNameSnapshot', record.characterNameSnapshot),
    ...stringSnapshot('actorNameSnapshot', record.actorNameSnapshot),
    ...stringSnapshot('actorEmailSnapshot', record.actorEmailSnapshot),
  };
}

function lineNotesFromSnapshot(snapshot: FirestoreCollectionSnapshot): LineNote[] {
  if (Array.isArray(snapshot.docs)) {
    return snapshot.docs.map(lineNoteFromSnapshot);
  }

  const notes: LineNote[] = [];
  const iterateDocuments = snapshot.forEach?.bind(snapshot);
  iterateDocuments?.(docSnap => {
    notes.push(lineNoteFromSnapshot(docSnap));
  });
  return notes;
}

function lineNotesCollection(scope: ProjectScope) {
  return collection(db, buildProjectSubcollectionPath(scope, 'lineNotes'));
}

export class FirebaseLineNotesClient implements LineNotesClient {
  async loadProjectLineNotes(scope: ProjectScope): Promise<LineNote[]> {
    const snapshot = await getDocs(lineNotesCollection(scope));
    return lineNotesFromSnapshot(snapshot as FirestoreCollectionSnapshot);
  }

  async loadLineNotesByDate(scope: ProjectScope, rehearsalDate: string): Promise<LineNote[]> {
    const lineNotesQuery = query(
      lineNotesCollection(scope),
      where('rehearsalDate', '==', rehearsalDate),
    );
    const snapshot = await getDocs(lineNotesQuery);
    return lineNotesFromSnapshot(snapshot as FirestoreCollectionSnapshot);
  }
}
